import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";

const STEP_ORDER = [
  "BASIC_INFO",
  "LOCATION",
  "CONTACT",
  "COMPLIANCE",
  "POS_CONFIGURATION",
] as const;

const STEP_TITLES: Record<string, string> = {
  BASIC_INFO: "Basic information",
  LOCATION: "Location",
  CONTACT: "Contact",
  COMPLIANCE: "Compliance & licenses",
  POS_CONFIGURATION: "POS configuration",
};

/** Human-readable labels for known field keys (snake_case). */
const FIELD_LABELS: Record<string, string> = {
  store_type: "Store type",
  trade_name: "Trade name",
  description: "Description",
  address_line1: "Address line 1",
  address_line2: "Address line 2",
  city: "City",
  state: "State / region",
  postal_code: "Postal code",
  country: "Country",
  phone: "Phone",
  alternate_phone: "Alternate phone",
  support_email: "Support email",
  website: "Website",
  gst_number: "GST number",
  pan_number: "PAN",
  drug_license_number: "Drug license number",
  excise_license_number: "Excise license number",
  fssai_license_number: "FSSAI license number",
  default_currency: "Default currency",
  tax_inclusive_pricing: "Tax-inclusive pricing",
  receipt_footer: "Receipt footer",
  low_stock_alert_enabled: "Low stock alerts",
};

function labelForKey(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">Not set</span>;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    return (
      <pre className="text-xs bg-muted/50 rounded-md p-2 overflow-auto max-w-full">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return String(value);
}

type ProfileData = Record<string, Record<string, unknown>>;

export function ProfileDataSections({ profileData }: { profileData: ProfileData | null | undefined }) {
  const data = profileData ?? {};
  const keysInOrder = STEP_ORDER.filter((k) => k in data && data[k] != null && typeof data[k] === "object");

  if (keysInOrder.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No step data has been saved yet.</p>
    );
  }

  return (
    <div className="space-y-4">
      {keysInOrder.map((stepKey) => {
        const section = data[stepKey] as Record<string, unknown>;
        const entries = Object.entries(section).filter(([k]) => k !== "_meta");
        const title = STEP_TITLES[stepKey] ?? stepKey.replace(/_/g, " ");

        return (
          <Card key={stepKey} className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{title}</CardTitle>
              <CardDescription className="font-mono text-xs">{stepKey}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {entries.map(([key, val]) => (
                  <div key={key} className="min-w-0 sm:col-span-2">
                    <dt className="text-xs font-medium text-muted-foreground mb-0.5">{labelForKey(key)}</dt>
                    <dd className="text-sm text-foreground break-words">{formatValue(val)}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
